import { useEffect, useRef, useState } from 'react'

import { Attachment, VisitLine } from 'api/types'
import { Button, IconButton } from 'components/ui'
import { t } from 'lib/i18n'
import { useObjectUrl } from 'lib/hooks'

import { newId } from './LinesEditor'

const recorderMime = () => {
  if (typeof MediaRecorder === 'undefined') return ''
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
  return types.find((type) => MediaRecorder.isTypeSupported(type)) ?? ''
}

const fileType = (mime: string) => mime.split(';')[0] || 'audio/webm'

const MomAudio = ({ attachment, onRemove }: { attachment: Attachment; onRemove?: () => void }) => {
  const url = useObjectUrl(`/attachments/${attachment.id}?inline=1`)
  return (
    <div className="lines__files">
      {url ? <audio controls src={url} preload="none" /> : <span className="spinner" aria-hidden="true" />}
      {onRemove ? <IconButton icon="trash" label={t('حذف التسجيل', 'Delete recording')} onClick={onRemove} /> : null}
    </div>
  )
}

const VoiceRecorder = ({ busy, disabled, onRecorded }: { busy: boolean; disabled: boolean; onRecorded: (file: File) => void }) => {
  const [recording, setRecording] = useState(false)
  const [error, setError] = useState('')
  const recorder = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])
  const stream = useRef<MediaStream | null>(null)
  const alive = useRef(true)

  useEffect(() => {
    return () => {
      alive.current = false
      if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop()
      stream.current?.getTracks().forEach((track) => track.stop())
    }
  }, [])

  const stop = () => {
    const active = recorder.current
    if (active && active.state !== 'inactive') active.stop()
  }

  const start = async () => {
    setError('')
    const mime = recorderMime()
    if (!mime || !navigator.mediaDevices?.getUserMedia) {
      setError(t('هذا المتصفح لا يدعم التسجيل الصوتي.', 'This browser cannot record audio.'))
      return
    }
    try {
      const next = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.current = next
      chunks.current = []
      const media = new MediaRecorder(next, { mimeType: mime })
      media.ondataavailable = (event) => {
        if (event.data.size) chunks.current.push(event.data)
      }
      media.onstop = () => {
        next.getTracks().forEach((track) => track.stop())
        stream.current = null
        if (alive.current) setRecording(false)
        const type = fileType(mime)
        const blob = new Blob(chunks.current, { type })
        if (!blob.size) return
        const extension = type.includes('mp4') ? 'm4a' : 'webm'
        onRecorded(new File([blob], `mom-${Date.now()}.${extension}`, { type }))
      }
      recorder.current = media
      media.start()
      setRecording(true)
    } catch {
      setError(t('تعذر استخدام الميكروفون.', 'The microphone could not be used.'))
    }
  }

  return (
    <div className="stack">
      <Button size="sm" variant={recording ? 'danger' : 'secondary'} icon="mic" busy={busy} disabled={disabled || busy} onClick={recording ? stop : start}>
        {recording ? t('إيقاف التسجيل', 'Stop recording') : t('تسجيل محضر الاجتماع', 'Record minutes of meeting')}
      </Button>
      {recording ? <p className="muted small">{t('جارٍ التسجيل… يُحفظ الملف مع هذه الزيارة.', 'Recording… the file is saved on this visit.')}</p> : null}
      {error ? <p className="muted small">{error}</p> : null}
    </div>
  )
}

export const VisitLines = ({
  lines,
  recordings,
  readOnly,
  uploadingId,
  onChange,
  onRecorded,
  onRemoveRecording,
}: {
  lines: VisitLine[]
  recordings: Attachment[]
  readOnly: boolean
  uploadingId?: string | null
  onChange: (lines: VisitLine[]) => void
  onRecorded: (line: VisitLine, file: File) => void
  onRemoveRecording: (attachment: Attachment) => void
}) => {
  const update = (index: number, patch: Partial<VisitLine>) => {
    const next = lines.slice()
    next[index] = { ...next[index], ...patch }
    onChange(next)
  }

  return (
    <div className="stack">
      {lines.length ? null : <p className="lines__empty">{t('لا توجد زيارات', 'No visits')}</p>}
      {lines.map((line, index) => {
        const files = recordings.filter((item) => item.lineId === line.id)
        return (
          <div key={line.id ?? index} className="card">
            <div className="stack">
              <label className="field">
                <span className="field__label">{t('الجهة الزائرة', 'Visiting party')}</span>
                <input className="input" value={line.party} readOnly={readOnly} onChange={(event) => update(index, { party: event.target.value })} />
              </label>
              <label className="field">
                <span className="field__label">{t('سبب الزيارة', 'Reason for the visit')}</span>
                <input className="input" value={line.reason ?? ''} readOnly={readOnly} onChange={(event) => update(index, { reason: event.target.value })} />
              </label>
              <label className="field">
                <span className="field__label">{t('محضر الاجتماع', 'Minutes of meeting')}</span>
                <textarea
                  className="input input--area"
                  rows={3}
                  readOnly={readOnly}
                  value={line.minutes ?? ''}
                  onChange={(event) => update(index, { minutes: event.target.value })}
                />
              </label>
              {files.map((file) => (
                <MomAudio key={file.id} attachment={file} onRemove={readOnly ? undefined : () => onRemoveRecording(file)} />
              ))}
              {readOnly ? null : (
                <VoiceRecorder busy={uploadingId === line.id} disabled={!line.id} onRecorded={(file) => onRecorded(line, file)} />
              )}
              {readOnly ? null : (
                <Button size="sm" variant="ghost" icon="trash" onClick={() => onChange(lines.filter((_, position) => position !== index))}>
                  {t('حذف الزيارة', 'Delete visit')}
                </Button>
              )}
            </div>
          </div>
        )
      })}
      {readOnly ? null : (
        <Button size="sm" variant="ghost" icon="plus" onClick={() => onChange([...lines, { id: newId(), minutes: '', party: '', reason: '' }])}>
          {t('إضافة زيارة', 'Add visit')}
        </Button>
      )}
    </div>
  )
}
